import "./model";
import { GVResult } from "./model";

const GV_STATE_IDLE = 1;
const GV_STATE_CREATING = 2;
const GV_STATE_FAILED = 3;
const GV_STATE_STARTED = 4;
const GV_STATE_DONE = 5;
const GV_STATE_WAIT_PROGRESS = 6;
const GV_STATE_WAIT_RESULTS = 7;

export class GVServerComm {
  /**
   * @public
   * @constructor
   * @returns {GVServerComm}
   */
  constructor() {
    /**
     * The interval between progress polls, in milliseconds
     * @type {number}
     */
    this.updatePeriod = 2000;

    /**
     * The search timeout, in milliseconds
     * @type {number}
     */
    this.timeout = 1000;

    this.engineURL = "/search";

    /**
     * @public
     * Lookup table from IATA airport codes to human readable names.
     * @type {Object<string,string>}
     */
    this.airportLookup = {};

    /**
     * @public
     * Lookup table from provider codes to human readable names.
     * @type {Object<string,string>}
     */
    this.providerLookup = {};

    /**
     * @public
     * Lookup table from airline codes to human readable names.
     * @type {Object<string,string>}
     */
    this.airlineLookup = {};

    /**
     * @public
     * The results that were returned by the search
     * @type {Object.<string,GVResult>}
     */
    this.results = {};

    // noinspection JSUnusedGlobalSymbols
    /**
     * @public
     * The flexible result data as generated by the engine.
     */
    this.flexData = null;

    /**
     * @public
     * The status for each result provider, indexed by provider code and represented as
     * engine status codes.
     * @type {Object<string,number>}
     */
    this.providerProgress = {};

    /**
     * @public
     * The progress of the search, as a number on the unit interval.
     * @type {number}
     */
    this.progress = 0;

    // noinspection JSUnusedGlobalSymbols
    /**
     * @public
     * The query currently being processed
     * @type {GVQuery}
     */
    this.currentQuery = null;

    /**
     * The state of the GVServerComm object
     * @private
     * @type {number}
     */
    this.state = GV_STATE_IDLE;
    this.progressInterval = null;
    this.progressCount = 0;
  }

  onAsyncWarning(error, info) {}

  onResultsUpdate() {}

  onEngineDone() {}

  onSessionCreated(sid) {}

  onProgressUpdate() {}

  onAsyncError(error, info) {}

  onEngineStarted() {}

  // noinspection JSUnusedGlobalSymbols
  /**
   * @public
   * Resets the state to allow a new search.
   */
  resetState() {
    this.state = GV_STATE_IDLE;
    // noinspection JSUnusedGlobalSymbols
    this.currentQuery = null;
    this.providerProgress = {};
    this.progressInterval = null;
    this.progressCount = 0;
    this.progress = 0;
  }

  /**
   * @private
   * Performs error handling and invokes onAsyncError
   * @param error
   * @param info
   */
  handleAsyncError(error, info) {
    this.state = GV_STATE_FAILED;
    this.onAsyncError(error, info);
  }

  /**
   * @private
   * Performs error handling and invokes onAsyncWarning
   * @param error
   * @param info
   */
  handleAsyncWarning(error, info) {
    this.onAsyncWarning(error, info);
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * @public
   * Creates a new search session
   * @param query {GVQuery} The query to be made.
   */
  createSession(query) {
    const that = this;

    if (this.state !== GV_STATE_IDLE) {
      throw "gv_busy";
    }

    // noinspection JSUnusedGlobalSymbols
    this.currentQuery = query;

    query.validate();

    this.state = GV_STATE_CREATING;

    const createURL = `${this.engineURL}/start/`;

    $.ajax(createURL, {
      success: (d) => that.createSessionCompleted(d),
      error: (a, s, e) =>
        that.handleAsyncError("gv_create_fail", {
          xhr: a,
          status: s,
          error: e,
        }),
      data: query,
      dataType: "json",
      method: "POST",
    });
  }

  /**
   * Callback function for create session
   */
  createSessionCompleted(data) {
    if (!data) {
      this.handleAsyncError("gv_create_bad_response", { data: data });
    }

    if (!data.status) {
      this.handleAsyncError(GVStatusCodeXlat[data.statusCode], data);
      return;
    }

    this.sessionId = data.sid;

    this.onSessionCreated(data.sid);

    this.handleEngineStarted();
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Starts the engine for this search.
   * @param {String|null} sid The id for the session to start, or null if the session was created by this instance.
   */
  resumeSession(sid) {
    if (this.state !== GV_STATE_IDLE) {
      throw "gv_invalid_state";
    }

    this.sessionId = sid;

    if (!this.sessionId) {
      throw "gv_session_not_set";
    }

    this.handleEngineStarted();
  }

  /**
   * @private
   * Handles completion of the engine session and fires onEngineDone.
   */
  handleEngineStarted() {
    const that = this;

    this.state = GV_STATE_STARTED;

    this.onEngineStarted();

    this.handleProgressInterval();
  }

  requestProgressSoon() {
    this.progressInterval = window.setInterval(() => {
      this.handleProgressInterval();
    }, this.updatePeriod);
  }

  /**
   * @private
   * Determines whether there are still any providers running
   */
  isEngineDone() {
    let hadResults = false;
    for (let pr in this.providerProgress) {
      if (!this.providerProgress.hasOwnProperty(pr)) continue;
      hadResults = true;
      let st = this.providerProgress[pr];
      if (st > 0 && st !== 10) {
        return false;
      }
    }
    return hadResults;
  }

  /**
   * @private
   * Handles completion of the engine session and fires onEngineDone.
   */
  handleEngineDone() {
    clearInterval(this.progressInterval);

    this.state = GV_STATE_DONE;

    this.onEngineDone();
  }

  /**
   * @private
   * Handles regular updates of the request progress.
   */
  handleProgressInterval() {
    const that = this;

    if (this.state !== GV_STATE_STARTED) {
      return;
    }

    this.state = GV_STATE_WAIT_PROGRESS;

    const progressURL = `${this.engineURL}/${this.sessionId}/data.json`;

    $.ajax(progressURL, {
      cache: false,
      data: {
        r: this.progressCount,
      },
      success: (d) => that.handleProgressData(d),
      error: function () {
        if (that.state === GV_STATE_WAIT_PROGRESS) {
          that.state = GV_STATE_STARTED;
        }
      },
      dataType: "json",
    });

    this.progressCount++;
    this.requestProgressSoon();
  }

  /**
   * @private
   * Handles the received progress data and fetches any new results.
   */
  handleProgressData(data) {
    if (this.state !== GV_STATE_WAIT_PROGRESS) {
      return;
    }

    if (!data) {
      this.state = GV_STATE_STARTED;
      this.handleAsyncWarning("gv_progress_null"); //TODO: Is this actually an error?
      return;
    }

    let newData = false;

    for (let i = 0; i < data.voortgang.length; i++) {
      const pr = data.voortgang[i];
      const os = this.providerProgress[pr.aanbieder];
      const ns = pr.status;
      if (os !== ns) {
        newData = newData || typeof os === "undefined" || os < ns;
        this.providerProgress[pr.aanbieder] = ns;
      }
    }

    this.progress = data.current / data.target;

    this.onProgressUpdate(newData);

    if (newData) {
      this.requestResults();
    } else if (this.isEngineDone()) {
      this.handleEngineDone();
    } else {
      this.state = GV_STATE_STARTED;
    }
  }

  /**
   * Requests new results from the engine.
   */
  requestResults() {
    let that = this;

    if (this.state === GV_STATE_WAIT_RESULTS) {
      return;
    }

    this.state = GV_STATE_WAIT_RESULTS;

    const resultURL = `${this.engineURL}/${this.sessionId}/results.json`;

    $.ajax(resultURL, {
      cache: false,
      data: {
        r: this.progressCount,
      },
      success: (d) => that.handleResults(d),
      error: function () {
        if (that.state === GV_STATE_WAIT_RESULTS) {
          if (that.isEngineDone()) {
            that.handleEngineDone();
          } else {
            that.state = GV_STATE_STARTED;
          }
        }
      },
      dataType: "json",
    });
  }

  /**
   * @private
   * Handles new results returned by the engine
   * @param data
   */
  handleResults(data) {
    if (!data) {
      this.handleAsyncWarning("gv_results_null"); //TODO: Is this actually an error?
      return;
    }

    $.extend(this.airportLookup, data.stad[0]);
    $.extend(this.providerLookup, data.aanbieder[0]);
    $.extend(this.airlineLookup, data.airline[0]);

    if (data.results.length !== 0) {
      this.results = {};

      for (let i = 0; i < data.results.length; i++) {
        const record = data.results[i];

        try {
          this.results[record.uniek] = new GVResult(record);
        } catch (ex) {
          this.handleAsyncWarning("gv_parse_result", {
            cause: ex,
            result: record,
          });
        }
      }

      // noinspection JSUnusedGlobalSymbols
      this.flexData = data.flexprijzen; //TODO: Wrap this.
      this.onResultsUpdate();
    }

    if (this.isEngineDone()) {
      this.handleEngineDone();
    } else {
      this.state = GV_STATE_STARTED;
    }
  }
}

export const GVStatusCodeXlat = {
  0: "gv_success",
  5: "gv_invalid_home",
  6: "gv_no_legs",
  11: "gv_past_date",
  12: "gv_leg_order",
  21: "gv_bad_origin",
  22: "gv_bad_dest",
  25: "gv_same_route",
  31: "gv_inv_adults",
  32: "gv_inv_children",
  33: "gv_inv_infants",
  34: "gv_inv_passengers",
  35: "gv_too_many_babies",
  80: "gv_engine_fail",
  91: "gv_bad_request",
};

export const GVStatusCodeTable = {
  /**
   * Everything is OK. No error.
   */
  ERROR_NO_ERROR: 0,

  /**
   * Invalid home specified
   */
  ERROR_INVALID_HOME: 5,

  /**
   * No legs specified
   */
  ERROR_NO_LEGS: 6,

  /**
   * Date is in the past.
   */
  ERROR_PAST_DATE: 11,

  /**
   * This date is before the date of a previous leg.
   */
  ERROR_DATE_BEFORE_PREVIOUS_LEG: 12,

  /**
   * Request origin not recognized. Suggestions may be available.
   */
  ERROR_INVALID_ORIGIN: 21,

  /**
   * Request destination not recognized. Suggestions may be available.
   */
  ERROR_INVALID_DESTINATION: 22,

  /**
   * Origin and destination are the same.
   */
  ERROR_SAME_ROUTE: 25,

  // Passenger related errors
  /**
   * The number of adults is not valid.
   */
  ERROR_INVALID_ADULTS: 31,

  /**
   * The number of children is not valid.
   */
  ERROR_INVALID_CHILDREN: 32,

  /**
   * The number of infants is not valid.
   */
  ERROR_INVALID_INFANTS: 33,

  /**
   * The number of passengers is not valid.
   */
  ERROR_NUMBER_OF_PASSENGERS: 34,

  /**
   * There's more babies than adults.
   */
  ERROR_MORE_BABIES_THAN_ADULTS: 35,

  /**
   * Generic error when the formatting for the request could not be understood. Generally not thrown by legitimate viewers.
   */
  ERROR_ENGINE_ERROR: 80,

  /**
   * Generic error when the formatting for the request could not be understood. Generally not thrown by legitimate viewers.
   */
  ERROR_INVALID_FORMAT: 91,
};
